import { useState, useMemo, useEffect } from "react";

import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useVisitsBreakdownData } from "../hooks/useVisitsBreakdownData";

import { processCompareModeData, createCompareModeLegend, createChartDataWithVisibility } from "./utils";

const useSelectedLobs = () => {
    const param = proNavigator.getParams()?.chartCategories as string;
    const selectedLobs = param?.split("|") || [];
    return useState<string[]>(selectedLobs);
};
const useVisitsBreakdownCompareMode = () => {
    const { data, isLoading } = useVisitsBreakdownData();

    const { lobEntries, availableLobs, lobVisitsMap, initialLegend } = useMemo(() => {
        const processedData = processCompareModeData(data);
        return {
            ...processedData,
            initialLegend: createCompareModeLegend(data),
        };
    }, [data]);

    const [legendItems, setLegendItems] = useState(initialLegend);
    const [selectedLobs, setSelectedLobs] = useSelectedLobs();

    useEffect(() => {
        setLegendItems(initialLegend);
    }, [initialLegend]);

    useEffect(() => {
        if (selectedLobs.length === 0 && lobEntries.length > 0 && !proNavigator.getParams()?.chartCategories) {
            const defaultSelectedLobs = lobEntries.slice(0, 5).map((entry) => entry.lob);
            setSelectedLobs(defaultSelectedLobs);
        }
    }, [selectedLobs.length, lobEntries, setSelectedLobs]);

    const { allChartData, visibleChartData } = useMemo(() => {
        const visibleDomains = new Set(legendItems.filter((l) => l.visible).map((l) => l.name));
        return createChartDataWithVisibility(lobEntries, visibleDomains, data, selectedLobs);
    }, [lobEntries, legendItems, data, selectedLobs]);

    const handleSelectedLobsChange = (newSelectedLobs: string[]) => {
        const validSelectedLobs = newSelectedLobs.filter((lob) => availableLobs.includes(lob));

        const shouldAutoInitialize =
            validSelectedLobs.length === 0 && lobEntries.length > 0 && !proNavigator.getParams()?.chartCategories;

        const finalSelectedLobs = shouldAutoInitialize
            ? lobEntries.slice(0, 5).map((entry) => entry.lob)
            : validSelectedLobs;

        setSelectedLobs(finalSelectedLobs);
    };

    const handleDropdownClose = () => {
        proNavigator.applyUpdateParams({ chartCategories: selectedLobs.join("|") }, { reload: false });
    };
    return {
        allChartData,
        legendItems,
        visibleChartData,
        setLegendItems,
        selectedLobs,
        handleSelectedLobsChange,
        handleDropdownClose,
        isLoading,
        availableLobs,
        lobVisitsMap,
    };
};

export default useVisitsBreakdownCompareMode;
